<template>
  <button
    type="button"
    class="share"
    :class="`is-${state}`"
    :aria-label="`Copy link to ${title}`"
    :disabled="state === 'working'"
    @click.stop.prevent="run"
  >
    <!-- Idle -->
    <svg
      v-if="state === 'idle'"
      class="share__icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      stroke-width="1.9"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <circle cx="18" cy="5" r="2.6" />
      <circle cx="6" cy="12" r="2.6" />
      <circle cx="18" cy="19" r="2.6" />
      <line x1="8.3" y1="10.8" x2="15.7" y2="6.2" />
      <line x1="8.3" y1="13.2" x2="15.7" y2="17.8" />
    </svg>

    <!-- Working. Held for a beat even when the copy was instant: an action that
         completes before it has been seen to start reads as one that did
         nothing at all. -->
    <svg
      v-else-if="state === 'working'"
      class="share__icon share__icon--spin"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      stroke-width="2.2"
      stroke-linecap="round"
      aria-hidden="true"
    >
      <path d="M12 3.4a8.6 8.6 0 1 1-6.08 2.52" />
    </svg>

    <!-- Done -->
    <svg
      v-else
      class="share__icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      stroke-width="2.2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <polyline points="4 12.5 9.5 18 20 6.5" />
    </svg>

    <span v-if="state === 'done'" class="share__word">{{ failed ? 'Failed' : 'Copied' }}</span>
  </button>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue';
import { copyLink } from '~/composables/useShare';
import { countShare } from '~/composables/useItemViews';

const props = defineProps({
  // 'piece' or 'clip'.
  kind: { type: String, required: true },
  id: { type: String, required: true },
  title: { type: String, default: '' },
});

// idle -> working -> done -> idle
const state = ref('idle');
const failed = ref(false);
let timer = null;

// The copy itself takes no measurable time. This is the acknowledgement, not
// the work: an action that finishes before it has been seen to start reads as
// one that did nothing at all.
const BEAT_MS = 900;
const HOLD_MS = 1600;

function run() {
  if (state.value === 'working') return;

  clearTimeout(timer);
  state.value = 'working';

  let outcome = 'failed';
  try {
    outcome = copyLink({ kind: props.kind, id: props.id });
  } catch {
    outcome = 'failed';
  }

  failed.value = outcome !== 'copied';

  if (outcome === 'copied') {
    // The counter is a bystander here, and a number that did not move is not
    // worth a stuck button.
    try {
      countShare(props.kind, props.id);
    } catch {
      // ignored
    }
  }

  timer = setTimeout(() => {
    state.value = 'done';
    timer = setTimeout(() => {
      state.value = 'idle';
    }, HOLD_MS);
  }, BEAT_MS);
}

onBeforeUnmount(() => clearTimeout(timer));
</script>

<style lang="scss" scoped>
.share {
  display: inline-flex;
  align-items: center;
  gap: 0.34rem;
  // Padding for the finger, a negative margin to give the layout back what the
  // padding took. A 16px glyph is a 16px target otherwise, and this one sits in
  // a corner of a photograph where a near miss does nothing at all.
  padding: 0.3rem;
  margin: -0.3rem;
  border: none;
  background: none;
  font: inherit;
  // Inherited rather than fixed. This sits on a photograph in the gallery and
  // on the page's own background in the library, and a hardcoded white is
  // invisible on the second one in light mode. The count badge beside it
  // already takes its colour this way.
  color: inherit;
  opacity: 0.75;
  cursor: pointer;
  // The strip these sit in is usually not a target; this takes its own back.
  pointer-events: auto;
  transition: opacity 0.2s ease-in-out;

  &:hover:not(:disabled),
  &:focus-visible {
    opacity: 1;
  }

  &:disabled {
    cursor: default;
  }

  &.is-done {
    opacity: 1;
  }
}

.share__icon {
  flex-shrink: 0;
  // Sized here rather than left to the attributes, so the three states cannot
  // drift apart and the size is one edit rather than three.
  width: 16px;
  height: 16px;
}

.share__icon--spin {
  animation: share-spin 0.7s linear infinite;
}

.share__word {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

@keyframes share-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .share__icon--spin {
    animation-duration: 2s;
  }
}
</style>
